import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/lib/toast';

export interface EbayConnectionStatus {
	user_id: string;
	ebay_user_id: string | null;
	scopes: string[];
	environment: string;
	connected_at: string;
	updated_at: string;
	refresh_token_expires_at: string | null;
	is_active: boolean;
}

export function useEbayConnection() {
	const { user } = useAuth();
	const queryClient = useQueryClient();

	const query = useQuery({
		queryKey: ['ebay-connection', user?.id],
		enabled: !!user?.id,
		queryFn: async (): Promise<EbayConnectionStatus | null> => {
			const { data, error } = await (supabase as any)
				.from('ebay_connection_status')
				.select('*')
				.maybeSingle();
			if (error) throw error;
			return data;
		}
	});

	const connect = useMutation({
		mutationFn: async () => {
			const { data, error } = await supabase.functions.invoke('ebay-oauth-start');
			if (error) throw error;
			if (!data?.authorize_url) throw new Error('No authorize URL returned');
			// Break out of any iframe (e.g. Lovable preview) — eBay sign-in blocks iframes
			try {
				if (window.top && window.top !== window.self) {
					window.top.location.href = data.authorize_url;
					return;
				}
			} catch {
				// Cross-origin top — fall back to opening in a new tab
				window.open(data.authorize_url, '_blank', 'noopener,noreferrer');
				return;
			}
			window.location.href = data.authorize_url;
		},
		onError: (err: any) => {
			toast.error(`Could not start eBay sign-in: ${err.message ?? err}`);
		}
	});

	const disconnect = useMutation({
		mutationFn: async () => {
			const { error } = await supabase.functions.invoke('ebay-disconnect');
			if (error) throw error;
		},
		onSuccess: () => {
			toast.success('eBay account disconnected');
			queryClient.invalidateQueries({ queryKey: ['ebay-connection'] });
		},
		onError: (err: any) => {
			toast.error(`Disconnect failed: ${err.message ?? err}`);
		}
	});

	return { ...query, connect, disconnect };
}

export function useEbaySyncOrders() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async () => {
			const { data, error } = await supabase.functions.invoke('ebay-sync-orders', { body: {} });
			if (error) throw error;
			return data as {
				ok: boolean;
				results: Array<{ orders_seen: number; transactions_created: number; errors: string[] }>;
			};
		},
		onSuccess: (data) => {
			const r = data?.results?.[0];
			const created = r?.transactions_created ?? 0;
			const errs = r?.errors ?? [];
			if (errs.length) {
				const msg = errs[0] ?? '';
				if (
					msg.includes('403') ||
					msg.toLowerCase().includes('access denied') ||
					msg.toLowerCase().includes('insufficient permissions')
				) {
					toast.error(
						'eBay denied order access. Please disconnect and reconnect eBay to grant order-read permission.'
					);
				} else {
					toast.error(`eBay sync issue: ${msg.slice(0, 140)}`);
				}
				return;
			}
			toast.success(
				created > 0
					? `Synced ${created} new eBay order${created === 1 ? '' : 's'}`
					: 'No new eBay orders'
			);
			queryClient.invalidateQueries({ queryKey: ['transactions'] });
			queryClient.invalidateQueries({ queryKey: ['user-items'] });
		},
		onError: (err: any) => {
			toast.error(`Could not sync eBay orders: ${err.message ?? err}`);
		}
	});
}
